import _ from 'lodash';
import { useEffect, useMemo, useState, type VoidFunctionComponent } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import useLicense from '../../../hooks/api/licenses/useLicense';
import { useRevisionDeepLicenses } from '../../../hooks/api/licenses/useRevisionDeepLicenses';
import { useRevisionLicenses } from '../../../hooks/api/licenses/useRevisionLicenses';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { getProjectBaseURLFromParams, isDeclaredLicense } from '../../../utils';
import Loader from '../../generic/Loader';
import { useRevision } from '../contexts/RevisionContext';
import type { IssueTypesSummary } from '../../../../shared/issuesV2';
import type { RevisionLicense } from '../../../../shared/licenses/common';
import type { Project } from '../../../types';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import CTASection from './CTASection';
import PreviewSection from './PreviewSection';
import ScanSummaryHeader from './ScanSummaryHeader';

const isUnknownLicense = (license: RevisionLicense) => license.licenseId === 'unknown';

/**
 * View when a public project is viewed from a user from another organization/who doesn't have access to it.
 */
const ProjectPreview: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams>();
  const project = useCurrentProject();
  const revision = useRevision();
  //  This is determined based on which licence has the most matches.
  //  Stored as state so that whenever it's not `null`, we kick off the useLicense query below.
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);

  //  If it's falsey, the queries will not be enabled.
  const licenses = useRevisionLicenses(revision.data?.locator ?? '');
  const deepLicenses = useRevisionDeepLicenses(revision.data?.locator ?? '');
  //  useLicense will not fetch the license if the selectedLicenseId is falsey
  //  This is being set after the licenses are fetched, down below in the useEffect
  const selectedLicense = useLicense(selectedLicenseId ?? '');

  const isAnyLicenseLoading = licenses.isFetching || deepLicenses.isFetching || selectedLicense.isFetching;

  //  Derived data (no need to store in state, but we memoize to avoid re-calculating it on every render)
  const deepestLevel = useMemo<number>(() =>
    Math.max(0, ...(deepLicenses.data?.map((license) => license.depth) ?? [0])), [deepLicenses.data]);

  //  We need to fetch the specific license, and we do that with this hook (via setting the selectedLicenseId state)
  useEffect(() => {
    if (!licenses.data?.length) {
      return;
    }
    let firstDeclaredLicenseId: string | undefined;
    for (const license of licenses.data) {
      if (license.matches?.some(isDeclaredLicense)) {
        firstDeclaredLicenseId = license.licenseId;
        break;
      }
    }
    //  End if we've found declared license
    if (firstDeclaredLicenseId) {
      setSelectedLicenseId(firstDeclaredLicenseId);
      return;
    }

    const knownLicenses = licenses.data?.filter(license => !isUnknownLicense(license));
    const dominantLicenseId = _.minBy(knownLicenses, (license) => license.matches?.length ?? 0)?.licenseId;

    setSelectedLicenseId(dominantLicenseId ?? null);
  }, [licenses]);

  //  Note that the revision is still using the Reflux store
  if (project.isLoading || revision.status.loading || isAnyLicenseLoading) {
    return <Loader />;
  }

  const baseURL = getProjectBaseURLFromParams(params);
  const issueCount = revision.data?.unresolved_issue_count ?? 0;

  return (
    <div className="container container-fluid-responsive margin-top margin-top-responsive">
      <ScanSummaryHeader
        baseURL={baseURL}
        deepestLevel={deepestLevel}
        issueCount={issueCount}
        dependencyCount={revision.data?.dependency_count ?? 0}
        licenseCount={revision.data?.license_count ?? 0}
        todoCount={revision.data?.todo_count ?? 0}
      />
      <PreviewSection
        baseURL={baseURL}
        deepestLevel={deepestLevel}
        issueCount={issueCount}
        unresolvedIssueCounts={revision.data?.unresolved_issue_counts ?? {} as IssueTypesSummary}
        project={project.data as Project}
        projectName={project.data?.title ?? ''}
        selectedLicense={selectedLicense.data ?? null}
        licenseCount={revision.data?.license_count ?? 0}
        dependencyCount={revision.data?.dependency_count ?? 0}
        isLimitedScan={project.data?.organization?.access_level !== 'premium'}
      />
      <CTASection />
    </div>
  );
};

export default ProjectPreview;
